import React from 'react';
import { motion } from 'framer-motion';

/**
 * TechnicalAnnotation — a JetBrains Mono label with a leader line and a
 * small target dot, styled like a plotted technical annotation on a
 * blueprint. Used for chapter markers, callouts, and labels-on-canvas.
 *
 * Props:
 *   code   — short ID such as "CH·02 / A" or "MOD-04"
 *   title  — main label text
 *   side   — 'left' (default) or 'right' — controls leader-line direction
 */
export default function TechnicalAnnotation({ code, title, side = 'left', className = '' }) {
  const isLeft = side === 'left';
  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -12 : 12 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`inline-flex items-center gap-3 ${className}`}
    >
      {isLeft && (
        <>
          <span
            className="block w-1.5 h-1.5 rounded-full shrink-0"
            style={{
              background: 'var(--scene-accent)',
              boxShadow: '0 0 8px var(--scene-accent)',
            }}
          />
          <span
            className="block h-px shrink-0"
            style={{ width: 38, background: 'var(--scene-accent)', opacity: 0.45 }}
          />
        </>
      )}
      <div className="leading-tight">
        {code && (
          <div
            className="font-mono text-[10px] sm:text-[11px] tracking-[0.32em] uppercase"
            style={{ color: 'var(--scene-accent)' }}
          >
            {code}
          </div>
        )}
        {title && (
          <div className="font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-white/55 mt-0.5">
            {title}
          </div>
        )}
      </div>
      {!isLeft && (
        <>
          <span
            className="block h-px shrink-0"
            style={{ width: 38, background: 'var(--scene-accent)', opacity: 0.45 }}
          />
          <span
            className="block w-1.5 h-1.5 rounded-full shrink-0"
            style={{
              background: 'var(--scene-accent)',
              boxShadow: '0 0 8px var(--scene-accent)',
            }}
          />
        </>
      )}
    </motion.div>
  );
}
